import React, { FC, useEffect } from 'react'

import { useForm, useStore } from '@tanstack/react-form'
import { AxiosError } from 'axios'
import {
  AuthButton,
  AuthButtonColor,
  AuthButtonVariant,
  AuthCheckbox,
  AuthInput,
  ErrorAlert
} from 'modules/auth/components'
import { Variants, motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useLoginMutation } from 'shared/api/auth/requests'
import { EASE_IN_OUT } from 'shared/constants/apps'
import { FEATURE_FLAGS } from 'shared/constants/featureFlags'
import { LocalStorageKey } from 'shared/constants/localStorage'
import { SessionStorageKey } from 'shared/constants/sessionStorage'
import { DEFAULT_REMEMBER_ME } from 'shared/constants/user'
import { getFieldError } from 'shared/utils'
import { lsRemoveItem, lsSetItem } from 'shared/utils/local-storage'

import styles from '../../LoginForm.module.scss'
import { regularLoginSchema } from './RegularLogin.schema'

const LOGIN_ERROR_TRANSLATION_KEYS = new Map<
  string,
  'backend_errors.invalid_credentials' | 'backend_errors.no_role_assigned'
>([
  [
    'Invalid email or password. Please verify your information and try again',
    'backend_errors.invalid_credentials'
  ],
  [
    'Login not possible because no role is assigned to your account',
    'backend_errors.no_role_assigned'
  ]
])

const fieldVariants: Variants = {
  hidden: { y: 12, scale: 0.98 },
  visible: {
    y: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: EASE_IN_OUT
    }
  }
}

const containerVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05
    }
  }
}

export const RegularLogin: FC = () => {
  const { t } = useTranslation('auth', {
    keyPrefix: 'new_login'
  })

  const loginMutation = useLoginMutation()

  const regularForm = useForm({
    defaultValues: {
      email: '',
      password: '',
      remember_me: DEFAULT_REMEMBER_ME
    },
    validators: {
      onMount: regularLoginSchema,
      onChange: regularLoginSchema,
      onSubmit: regularLoginSchema
    },
    onSubmit: values => {
      if (FEATURE_FLAGS.SHOW_REMEMBER_ME) {
        try {
          if (values.value.remember_me) {
            lsSetItem(LocalStorageKey.REMEMBER_ME, true)
          } else {
            lsRemoveItem(LocalStorageKey.REMEMBER_ME)
          }
        } catch {
          // Swallow storage errors to avoid blocking login
        }
      }
      loginMutation.mutate({
        email: values.value.email,
        password: values.value.password,
        remember_me: values.value.remember_me
      })
    }
  })

  const isSubmitted = useStore(
    regularForm.store,
    state => state.submissionAttempts > 0
  )
  const formValues = useStore(regularForm.store, state => state.values)
  const isFormValid = Boolean(
    formValues.email?.trim() && formValues.password?.trim()
  )
  const loginError = loginMutation.error as AxiosError<{
    error?: string
  }> | null
  const responseError = loginError?.response?.data?.error?.trim()
  const responseErrorTranslationKey = responseError
    ? LOGIN_ERROR_TRANSLATION_KEYS.get(responseError)
    : undefined
  const errorMessage =
    (responseErrorTranslationKey && t(responseErrorTranslationKey)) ||
    responseError ||
    (loginError?.response?.status === 401
      ? t('invalid_login')
      : loginError
        ? t('login.social_login_error.subtitle')
        : '')
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (loginMutation.isPending) return
    regularForm.handleSubmit()
  }

  useEffect(() => {
    const email = sessionStorage.getItem(
      SessionStorageKey.ACCOUNT_ACTIVATE_EMAIL
    )

    if (email) {
      regularForm.setFieldValue('email', email)
      sessionStorage.removeItem(SessionStorageKey.ACCOUNT_ACTIVATE_EMAIL)
    }
  }, [regularForm])

  return (
    <form className={styles.loginForm} onSubmit={handleSubmit}>
      <motion.div
        className={styles.inputs}
        initial='hidden'
        animate='visible'
        variants={containerVariants}
      >
        <motion.div variants={fieldVariants} className={styles.inputWrapper}>
          <regularForm.Field name='email'>
            {field => {
              const error = isSubmitted
                ? getFieldError(field, {
                    isSubmitted,
                    requireTouched: true
                  })
                : undefined
              return (
                <AuthInput
                  placeholder={t('login.email_placeholder')}
                  className={styles.input}
                  value={field.state.value}
                  onChange={e => {
                    field.handleChange(e.target.value)
                    if (isSubmitted) {
                      field.validate('change')
                    }
                  }}
                  error={error}
                />
              )
            }}
          </regularForm.Field>
        </motion.div>

        <motion.div variants={fieldVariants} className={styles.inputWrapper}>
          <regularForm.Field name='password'>
            {field => {
              const error = isSubmitted
                ? getFieldError(field, {
                    isSubmitted,
                    requireTouched: true
                  })
                : undefined
              return (
                <AuthInput
                  placeholder={t('login.password_placeholder')}
                  className={styles.input}
                  value={field.state.value}
                  onChange={e => {
                    field.handleChange(e.target.value)
                    if (isSubmitted) {
                      field.validate('change')
                    }
                  }}
                  type='password'
                  showTogglePassword
                  error={error}
                />
              )
            }}
          </regularForm.Field>
        </motion.div>
        <ErrorAlert message={errorMessage} />
      </motion.div>
      <motion.div
        className={styles.rememberMe}
        initial={{ y: 8, scale: 0.98 }}
        animate={{
          y: 0,
          scale: 1,
          transition: {
            delay: 0.15,
            duration: 0.4,
            ease: EASE_IN_OUT
          }
        }}
      >
        {FEATURE_FLAGS.SHOW_REMEMBER_ME && (
          <regularForm.Field name='remember_me'>
            {field => {
              const error = getFieldError(field, {
                isSubmitted,
                requireTouched: true
              })
              return (
                <AuthCheckbox
                  labelClassName={styles.rememberMeCheckbox}
                  label={t('login.remember_me')}
                  checked={field.state.value}
                  onChange={e => {
                    field.handleChange(e.target.checked)
                    if (isSubmitted) {
                      field.validate('change')
                    }
                  }}
                  error={error}
                />
              )
            }}
          </regularForm.Field>
        )}
        <Link to='/forgot-password' className={styles.forgotPassword}>
          {t('login.forgot_password')}
        </Link>
      </motion.div>
      <AuthButton
        className={styles.loginButton}
        label={t('login.button_text')}
        variant={AuthButtonVariant.FILLED_BLUE}
        colorType={AuthButtonColor.GHOST_DARKER}
        isLoading={loginMutation.isPending}
        fullWidth={true}
        isBold
        disabled={!isFormValid || loginMutation.isPending}
        type='submit'
      />
    </form>
  )
}
